import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

export type FixtureVersion = {
  version: string;
  documents: number | null;
  /** The set's documents, by name — what a run names to measure on a subset of them. */
  documentNames: string[];
  createdAt: string | null;
  aliases: string[];
  note: string;
  /** The version a run is measured on unless it asks for another. */
  pinned: boolean;
};

export type ArenaTask = {
  taskKey: string;
  fixtures: {
    name: string;
    location: string;
    version: string;
    documents: number | null;
    createdAt: string | null;
  };
  /** Newest first. Empty when W&B could not be reached, which means the pinned version is the only
   * one on offer — not that it is the only one there is. */
  fixtureVersions: FixtureVersion[];
  rankMetric: string | null;
  scoringModels: string[];
};

export type ArenaRunStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export type CandidateResult = {
  modelName: string;
  modelProvider: string;
  rank: number | null;
  metrics: Record<string, number | string>;
  calls: number;
  callsServed: number;
  fallbackRate: number;
  servedByOthers: Record<string, number>;
  failures: number;
  breakdowns: Record<string, { columns: string[]; rows: (string | number)[][] }>;
  wallClockSeconds: number | null;
};

export type ArenaRun = {
  id: string;
  taskKey: string;
  status: ArenaRunStatus;
  tenantId: string;
  triggeredBy: string | null;
  candidates: string[];
  fixtures: { name: string; location: string; version: string };
  scoring: Record<string, string | number>;
  /** Which documents this run measured on, by name, or null for all of them. */
  fixtureDocuments: string[] | null;
  notBefore: string;
  startedAt: string | null;
  finishedAt: string | null;
  failureReason: string | null;
  wandbRunUrl: string | null;
  results: CandidateResult[];
};

type CreateRunArgs = {
  taskKey: string;
  candidates: { modelId: string }[];
  scoring: Record<string, string | number>;
  /** Omitted means the pinned version, which is what keeps a leaderboard comparable. */
  fixtureVersion?: string;
  /** Omitted means every document in the set. */
  fixtureDocuments?: string[];
  notBefore?: string;
};

/** Shows a failure in the banner at the top of the tab. */
export type ReportError = (title: string, message: string) => void;

const runsKey = (taskKey: string | null) => ['admin', 'model-arena', 'runs', { taskKey }];
const inFlightKey = ['admin', 'model-arena', 'runs', 'in-flight'];

const invalidateRuns = (queryClient: QueryClient, taskKey: string) => {
  queryClient.invalidateQueries({ queryKey: runsKey(taskKey) });
  queryClient.invalidateQueries({ queryKey: inFlightKey });
};

/** Every write reports its own failure, so no caller has to remember to; the boolean says whether
 * the caller's success path should run. */
const reported = async (
  reportError: ReportError,
  title: string,
  write: () => Promise<unknown>,
): Promise<boolean> => {
  try {
    await write();
    return true;
  } catch (cause) {
    reportError(title, cause instanceof Error ? cause.message : 'Unknown error');
    return false;
  }
};

type ArenaCapability = { mayRun: boolean; environment: string; reason: string | null };

export const useArenaCapability = () => {
  const { data } = useQuery<ArenaCapability>({
    queryKey: ['admin', 'model-arena', 'capability'],
  });

  return { capability: data };
};

export const useArenaTasks = () => {
  const { data, isLoading, isError, error } = useQuery<ArenaTask[]>({
    queryKey: ['admin', 'model-arena', 'tasks'],
  });

  return { tasks: data, isLoading, isError, error };
};

export const useArenaRuns = (taskKey: string | null) => {
  const { data, isLoading, isError, error, refetch } = useQuery<ArenaRun[]>({
    queryKey: runsKey(taskKey),
    enabled: !!taskKey,
    // A queued run is picked up by a worker, so the list changes from outside the browser — but
    // only while something is still in flight. Settled history does not move.
    refetchInterval: query =>
      (query.state.data ?? []).some(run => run.status === 'QUEUED' || run.status === 'RUNNING')
        ? 15_000
        : false,
  });

  return { runs: data, isLoading, isError, error, refetch };
};

export const useArenaRunsInFlight = () => {
  const { data, isLoading } = useQuery<ArenaRun[]>({
    queryKey: inFlightKey,
    // Cheap — one table read, no W&B — so it can be asked often enough that a run appearing or
    // starting shows up while someone is still looking at the page.
    refetchInterval: 5_000,
  });

  return { inFlight: data ?? [], isLoading };
};

export const useCreateArenaRun = (reportError: ReportError) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<{ data: ArenaRun }, Error, CreateRunArgs>({
    mutationFn: args => api.post<ArenaRun>('admin/model-arena/runs', args),
    onSuccess: ({ data }) => invalidateRuns(queryClient, data.taskKey),
  });

  const createRun = (args: CreateRunArgs) =>
    reported(reportError, 'Could not queue the run', () => mutateAsync(args));

  return { createRun, isQueueing: isPending };
};

export const useDeleteArenaRun = (reportError: ReportError) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    unknown,
    Error,
    { runId: string; taskKey: string }
  >({
    mutationFn: ({ runId }) => api.delete(`admin/model-arena/runs/${runId}`),
    onSuccess: (_result, { taskKey }) => invalidateRuns(queryClient, taskKey),
  });

  const deleteRun = (runId: string, taskKey: string) =>
    reported(reportError, 'Could not delete the run', () => mutateAsync({ runId, taskKey }));

  return { deleteRun, isDeleting: isPending };
};

export const useCancelArenaRun = (reportError: ReportError) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<{ data: ArenaRun }, Error, { runId: string }>({
    mutationFn: ({ runId }) => api.post<ArenaRun>(`admin/model-arena/runs/${runId}/cancel`, {}),
    onSuccess: ({ data }) => invalidateRuns(queryClient, data.taskKey),
  });

  const cancelRun = (runId: string) =>
    reported(reportError, 'Could not cancel the run', () => mutateAsync({ runId }));

  return { cancelRun, isCancelling: isPending };
};
